import { formatSchoolName } from './format';

export function mapMetadataToSelectItems(metadataObject) {
	if (!metadataObject || typeof metadataObject !== 'object') {
		return [];
	}
	return Object.keys(metadataObject).map((key) => ({
		id: /^\d+$/.test(key) ? Number(key) : key,
		name: metadataObject[key],
	}));
}

export function getMetadataLabel(metadataObject, id) {
	if (!metadataObject || id === null || id === undefined) {
		return '';
	}
	return metadataObject[String(id)] || metadataObject[id] || '';
}

function normalizeDeclarantLabel(label) {
	return String(label || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

export const PARENT_DECLARANT_TYPE_ID = 2;
export const COLLEGE_DECLARANT_TYPE_ID = 3;

export function resolveParentDeclarantTypeId(metadata) {
	const declarantTypes = metadata && metadata.declarant_type;
	if (!declarantTypes || typeof declarantTypes !== 'object') {
		return PARENT_DECLARANT_TYPE_ID;
	}
	const entries = Object.entries(declarantTypes);
	const combined = entries.find(([, label]) => {
		const normalized = normalizeDeclarantLabel(label);
		return normalized.includes('родитель') && normalized.includes('законн');
	});
	if (combined) {
		return /^\d+$/.test(combined[0]) ? Number(combined[0]) : combined[0];
	}
	if (declarantTypes[String(PARENT_DECLARANT_TYPE_ID)] != null) {
		return PARENT_DECLARANT_TYPE_ID;
	}
	if (declarantTypes['3'] != null) {
		return 3;
	}
	const first = entries[0];
	if (!first) {
		return PARENT_DECLARANT_TYPE_ID;
	}
	return /^\d+$/.test(first[0]) ? Number(first[0]) : first[0];
}

export function getParentDeclarantTypeLabel(metadata, declarantTypeId) {
	const id = declarantTypeId != null && declarantTypeId !== ''
		? declarantTypeId
		: resolveParentDeclarantTypeId(metadata);
	return getMetadataLabel(metadata && metadata.declarant_type, id);
}

export function resolveCollegeDeclarantTypeId(metadata) {
	const declarantTypes = metadata && metadata.declarant_type;
	if (!declarantTypes || typeof declarantTypes !== 'object') {
		return COLLEGE_DECLARANT_TYPE_ID;
	}
	const entries = Object.entries(declarantTypes);
	const commission = entries.find(([, label]) => {
		const normalized = normalizeDeclarantLabel(label);
		return normalized.includes('приемн') || normalized.includes('комисс');
	});
	if (commission) {
		return /^\d+$/.test(commission[0]) ? Number(commission[0]) : commission[0];
	}
	if (declarantTypes[String(COLLEGE_DECLARANT_TYPE_ID)] != null) {
		return COLLEGE_DECLARANT_TYPE_ID;
	}
	const last = entries[entries.length - 1];
	if (!last) {
		return COLLEGE_DECLARANT_TYPE_ID;
	}
	return /^\d+$/.test(last[0]) ? Number(last[0]) : last[0];
}

export function getCollegeDeclarantTypeLabel(metadata, declarantTypeId) {
	const id = declarantTypeId != null && declarantTypeId !== ''
		? declarantTypeId
		: resolveCollegeDeclarantTypeId(metadata);
	return getMetadataLabel(metadata && metadata.declarant_type, id);
}

export function buildStep1ApplicationTypes(metadata) {
	if (!metadata || !metadata.training_type || !metadata.payment_type) {
		return [];
	}
	return [
		{
			training_type_id: 1,
			payment_type_id: 2,
			title: metadata.training_type['1'],
			subtitle: metadata.payment_type['2'],
		},
		{
			training_type_id: 2,
			payment_type_id: 2,
			title: metadata.training_type['2'],
			subtitle: metadata.payment_type['2'],
		},
		{
			training_type_id: 1,
			payment_type_id: 1,
			title: metadata.training_type['1'],
			subtitle: metadata.payment_type['1'],
		},
	].filter((item) => item.title && item.subtitle);
}

const SCHOOL_LIST_REGION_IDS = [8, 15];
const SCHOOL_LIST_REGION_NUMBERS = ['23', '47'];
const STEP6_REGION_IDS = SCHOOL_LIST_REGION_IDS;
const STEP6_REGION_NUMBERS = SCHOOL_LIST_REGION_NUMBERS;

export function isSchoolListRegion(regionId, regionList = []) {
	if (isEmptyDisplayValue(regionId)) {
		return false;
	}

	const normalizedId = String(regionId).trim();
	if (SCHOOL_LIST_REGION_IDS.includes(Number(normalizedId))) {
		return true;
	}
	if (SCHOOL_LIST_REGION_NUMBERS.includes(normalizedId)) {
		return true;
	}

	const region = regionList.find((item) => (
		String(item.id) === normalizedId
		|| String(item.ab) === normalizedId
		|| String(item.region_number) === normalizedId
	));
	if (!region) {
		return false;
	}

	return SCHOOL_LIST_REGION_IDS.includes(Number(region.id))
		|| SCHOOL_LIST_REGION_NUMBERS.includes(String(region.ab || region.region_number));
}

export function getSchoolEducationDisplayInfo(order, regionList = []) {
	const normalizedRegionList = prepareRegionListForLookup(regionList);
	const source = getOrderEducationSource(order);
	const regionId = getSchoolRegionId(source, order);
	const regionName = resolveSchoolRegionName(order, normalizedRegionList);

	let showSchoolName = isSchoolListRegion(regionId, normalizedRegionList);
	if (!showSchoolName && !isEmptyDisplayValue(regionName)) {
		const matchedRegion = normalizedRegionList.find((item) => (
			String(item.name).trim() === String(regionName).trim()
		));
		if (matchedRegion) {
			showSchoolName = isSchoolListRegion(
				matchedRegion.id ?? matchedRegion.region_id,
				normalizedRegionList,
			);
		}
	}

	return {
		regionName,
		schoolName: resolveSchoolName(order),
		showSchoolName,
	};
}

export function prepareRegionListForLookup(regionList) {
	if (Array.isArray(regionList)) {
		return normalizeRegions(regionList);
	}
	return normalizeRegions(normalizeRegionListResponse(regionList));
}

export function normalizeRegions(regions) {
	if (!Array.isArray(regions)) {
		return [];
	}
	return regions.map((region) => ({
		...region,
		ab: region.ab || region.region_number,
	}));
}

export function filterStep6Regions(regions) {
	if (!Array.isArray(regions)) {
		return [];
	}
	return normalizeRegions(regions)
		.filter((region) => (
			STEP6_REGION_IDS.includes(region.id)
			|| STEP6_REGION_NUMBERS.includes(String(region.ab || region.region_number))
		));
}

export function formatOrderDate(isoDate) {
	if (!isoDate) {
		return '';
	}
	const date = new Date(isoDate);
	if (Number.isNaN(date.getTime())) {
		return isoDate;
	}
	return date.toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

export function mapOrderToListItem(order, metadata, t) {
	const fullName = [
		order.abiturient_surname,
		order.abiturient_name,
		order.abiturient_lastname,
	].filter(Boolean).join(' ');

	const hasQuota = order.quota_type !== null && order.quota_type !== undefined;

	return {
		id: order.id,
		full_name: fullName,
		created_at: formatOrderDate(order.created_at),
		payment_type_name: getMetadataLabel(metadata.payment_type, order.payment_type),
		region_name: getMetadataLabel(metadata.training_type, order.training_type),
		education_type_name: getMetadataLabel(metadata.education_type, order.education_type),
		quota: hasQuota ? t('Есть') : t('Нет'),
		status_name: getMetadataLabel(metadata.status, order.status),
		from_site: 'EDUS',
	};
}

export function parseQualificationCodeKey(codeKey) {
	const match = String(codeKey).match(/^(.+?)\((.+)\)$/);
	if (!match) {
		return {
			qualification_code: codeKey,
			specialty_code: '',
			display_codes: codeKey,
		};
	}
	const qualificationCode = match[1];
	const specialtyCode = match[2];
	return {
		qualification_code: qualificationCode,
		specialty_code: specialtyCode,
		display_codes: `${qualificationCode} (${specialtyCode})`,
	};
}

export function formatQualificationCodeDisplay(code) {
	const { qualification_code: qualificationCode } = parseQualificationCodeKey(code || '');
	return qualificationCode || '—';
}

export function normalizeCollegeLangEntries(lang) {
	return (lang || []).map((entry) => {
		if (typeof entry === 'string') {
			return { lang_short: entry, is_have_target_grant: null };
		}
		const langShort = entry.lang_short || entry.lang || entry.code || entry.study_lang;
		return {
			lang_short: langShort,
			is_have_target_grant: entry.is_have_target_grant ?? entry.target_grant ?? entry.is_have ?? null,
		};
	}).filter((entry) => entry.lang_short);
}

export function getCollegeLangTargetGrantAvailability(college, langShort) {
	if (!college || !langShort) {
		return false;
	}
	const entries = college.langEntries || normalizeCollegeLangEntries(college.lang);
	const langEntry = entries.find((entry) => entry.lang_short === langShort);
	if (langEntry && langEntry.is_have_target_grant != null) {
		return !!langEntry.is_have_target_grant;
	}
	return true;
}

export function mapQualificationApiResponse(data) {
	if (!Array.isArray(data)) {
		return [];
	}
	const result = [];
	data.forEach((entry) => {
		const codeKey = Object.keys(entry)[0];
		if (!codeKey) {
			return;
		}
		const qualification = entry[codeKey];
		const { specialty_code, display_codes } = parseQualificationCodeKey(codeKey);
		result.push({
			qualification_code: codeKey,
			specialty_code,
			qualification_id: qualification.id,
			name: qualification.name,
			display_label: `${display_codes} - ${qualification.name}`,
			colleges: (qualification.colleges || []).map((college) => {
				const collegeLangEntries = normalizeCollegeLangEntries(college.lang);
				return {
					college_id: college.college_id,
					name: college.name,
					lang: collegeLangEntries.map((langEntry) => langEntry.lang_short),
					langEntries: collegeLangEntries,
				};
			}),
		});
	});
	return result;
}

export function mapCollegeLangOptions(langCodes, metadata) {
	return (langCodes || []).map((langShort) => ({
		lang_short: langShort,
		name: getMetadataLabel(metadata.study_lang, langShort) || langShort,
	}));
}

export function buildQualificationFormBody(requestData) {
	const body = new URLSearchParams();
	body.append('region_id', requestData.region_ab);
	body.append('payment_type', requestData.payment_type_id);
	body.append('education_type', requestData.education_type_id);
	body.append('education_form', requestData.education_form_id);
	body.append('training_type', requestData.training_type_id);
	return body;
}

const EDUCATION_TYPE_DOCUMENT_IDS = {
	1: [1],
	2: [1],
	3: [2, 3],
};

export function buildDefaultEducationRelations(metadata) {
	const docName = (id) => getMetadataLabel(metadata && metadata.education_document, id);
	return Object.entries(EDUCATION_TYPE_DOCUMENT_IDS).map(([educationType, documentIds]) => ({
		education_type: Number(educationType),
		documents: documentIds.map((educationDocument) => ({
			education_document: educationDocument,
			name: docName(educationDocument),
		})),
	}));
}

function normalizeDisplayValue(value) {
	if (value === null || value === undefined || value === '') {
		return '—';
	}
	const normalized = String(value).trim();
	if (!normalized || normalized === 'undefined' || normalized === 'null') {
		return '—';
	}
	return value;
}

function infoOrderField(name, value) {
	return {
		name,
		value: normalizeDisplayValue(value),
		type: 'text',
	};
}

function getOrderRejectReason(order) {
	const qualifications = order?.request_qualifications;
	if (!Array.isArray(qualifications)) {
		return null;
	}

	const qualification = qualifications.find((item) => item.reject_reason);
	const reason = qualification?.reject_reason;
	if (reason == null || !String(reason).trim()) {
		return null;
	}

	return String(reason).trim();
}

function infoOrderFlagField(name, value, t) {
	return infoOrderField(name, value ? t('Да') : t('Нет'));
}

function infoOrderFileField(name, file) {
	if (file === null || file === undefined || file === '') {
		return infoOrderField(name, null);
	}

	if (typeof file === 'object') {
		const url = file.url || file.link || file.path || file.value || '';
		const fileName = file.name || file.file_name || file.filename || '';
		if (!url) {
			return infoOrderField(name, fileName || null);
		}
		return {
			name,
			value: url,
			file_name: fileName || url.split('/').pop() || 'file',
			type: 'file',
		};
	}

	const fileStr = String(file);
	if (/^https?:\/\//.test(fileStr) || fileStr.startsWith('/')) {
		return {
			name,
			value: fileStr,
			file_name: fileStr.split('/').pop() || fileStr,
			type: 'file',
		};
	}

	if (/\.\w+$/.test(fileStr)) {
		return {
			name,
			value: fileStr,
			file_name: fileStr,
			type: 'file',
		};
	}

	if (/^\d+$/.test(fileStr)) {
		return {
			name,
			value: `/api/abiturient/file?id=${fileStr}`,
			file_name: fileStr,
			type: 'file',
		};
	}

	return infoOrderField(name, file);
}

function formatInfoOrderFullName(parts) {
	const fullName = parts.filter((part) => part !== null && part !== undefined && part !== '').join(' ').trim();
	return fullName || '—';
}

function formatInfoOrderDate(value) {
	if (!value) {
		return '—';
	}
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}
	return date.toLocaleDateString('ru-RU', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});
}

function isKazakhstanSchoolCountry(country) {
	if (country === null || country === undefined || country === '') {
		return false;
	}
	const value = String(country).trim().toLowerCase();
	return value === 'казахстан' || value === '398' || value === 'kazakhstan';
}

function isEmptyDisplayValue(value) {
	return value === null
		|| value === undefined
		|| value === ''
		|| String(value).trim() === 'undefined'
		|| String(value).trim() === 'null';
}

function pickFirstDefined(...values) {
	return values.find((value) => !isEmptyDisplayValue(value));
}

export function normalizeRegionListResponse(response) {
	const data = response?.data?.data ?? response?.data ?? response ?? [];
	if (Array.isArray(data)) {
		return data;
	}
	if (data && typeof data === 'object') {
		return Object.values(data);
	}
	return [];
}

function getOrderEducationSource(order) {
	if (!order) {
		return {};
	}
	const education = order.education && typeof order.education === 'object' ? order.education : null;
	if (!education) {
		return order;
	}
	return {
		...order,
		...education,
		school: pickFirstDefined(education.school, order.school),
		school_region: pickFirstDefined(education.school_region, order.school_region),
		school_region_name: pickFirstDefined(education.school_region_name, order.school_region_name),
		school_country: pickFirstDefined(education.school_country, order.school_country),
		school_region_id: pickFirstDefined(
			education.school_region_id,
			order.school_region_id,
			education.school_region_ab,
			order.school_region_ab,
			education.school?.region_id,
			order.school?.region_id,
			education.school?.region_ab,
			order.school?.region_ab,
		),
		school_region_ab: pickFirstDefined(education.school_region_ab, order.school_region_ab),
	};
}

function getOrderSchoolCountry(order) {
	return getOrderEducationSource(order).school_country;
}

function getSchoolRegionId(source, order = null) {
	const schoolObject = source.school && typeof source.school === 'object' ? source.school : null;
	const schoolRegionValue = pickFirstDefined(
		source.school_region,
		order?.school_region,
	);
	const numericSchoolRegion = schoolRegionValue != null && /^\d+$/.test(String(schoolRegionValue).trim())
		? schoolRegionValue
		: null;

	return pickFirstDefined(
		source.school_region_id,
		order?.school_region_id,
		source.school_region_ab,
		order?.school_region_ab,
		schoolObject?.region_id,
		schoolObject?.region_ab,
		numericSchoolRegion,
	);
}

export function findRegionNameById(regionId, regionList) {
	if (isEmptyDisplayValue(regionId) || !Array.isArray(regionList)) {
		return '';
	}
	const normalizedRegionId = String(regionId).trim();

	const matchById = regionList.find((item) => (
		String(item.id) === normalizedRegionId
		|| String(item.region_id) === normalizedRegionId
	));
	if (matchById && !isEmptyDisplayValue(matchById.name)) {
		return String(matchById.name).trim();
	}

	const matchByCode = regionList.find((item) => (
		String(item.ab) === normalizedRegionId
		|| String(item.region_number) === normalizedRegionId
	));
	if (matchByCode && !isEmptyDisplayValue(matchByCode.name)) {
		return String(matchByCode.name).trim();
	}

	const matchByName = regionList.find((item) => (
		String(item.name).trim() === normalizedRegionId
	));
	return matchByName && !isEmptyDisplayValue(matchByName.name)
		? String(matchByName.name).trim()
		: '';
}

export function findRegionByValue(regionValue, regionList = []) {
	if (isEmptyDisplayValue(regionValue) || !Array.isArray(regionList)) {
		return null;
	}
	const normalizedValue = String(regionValue).trim();

	const matchById = regionList.find((item) => (
		String(item.id) === normalizedValue
		|| String(item.region_id) === normalizedValue
	));
	if (matchById) {
		return matchById;
	}

	return regionList.find((item) => (
		String(item.ab) === normalizedValue
		|| String(item.region_number) === normalizedValue
	)) || null;
}

function resolveSchoolRegionName(order, regionList = []) {
	const normalizedRegionList = prepareRegionListForLookup(regionList);
	const source = getOrderEducationSource(order);
	const schoolObject = source.school && typeof source.school === 'object' ? source.school : null;
	const regionId = getSchoolRegionId(source, order);

	if (!isEmptyDisplayValue(regionId)) {
		const fromList = findRegionNameById(regionId, normalizedRegionList);
		if (!isEmptyDisplayValue(fromList)) {
			return fromList;
		}

		const region = findRegionByValue(regionId, normalizedRegionList);
		if (region && !isEmptyDisplayValue(region.name)) {
			return String(region.name).trim();
		}

		const regionIdString = String(regionId).trim();
		if (!/^\d+$/.test(regionIdString)) {
			return regionIdString;
		}
	}

	const directCandidates = [
		source.school_region_name,
		order?.school_region_name,
		schoolObject?.region_name,
		schoolObject?.region,
		source.school_region,
		order?.school_region,
	];

	for (const candidate of directCandidates) {
		if (isEmptyDisplayValue(candidate)) {
			continue;
		}
		const candidateString = String(candidate).trim();
		if (/^\d+$/.test(candidateString)) {
			const fromList = findRegionNameById(candidateString, normalizedRegionList);
			if (!isEmptyDisplayValue(fromList)) {
				return fromList;
			}
			continue;
		}
		return candidateString;
	}

	return '';
}

function resolveSchoolName(order) {
	const source = getOrderEducationSource(order);
	if (source.school && typeof source.school === 'object') {
		return formatSchoolName(source.school.name);
	}
	return formatSchoolName(source.school);
}

// function getInfoOrderSchoolInfo(order, regionList = []) {
// 	return {
// 		name: resolveSchoolName(order),
// 		region: resolveSchoolRegionName(order, regionList),
// 	};
// }

export function canWithdrawOrder(order) {
	const statusId = Number(order.status_id);
	if (!Number.isNaN(statusId) && statusId > 0) {
		return ![5, 6, 7].includes(statusId);
	}

	const status = String(order.status || '').toLowerCase();
	const blockedStatuses = ['отозван', 'принят', 'отказан'];
	return !blockedStatuses.some((word) => status.includes(word));
}

export function canEditOrder(order) {
	const statusId = Number(order.status_id);
	if (!Number.isNaN(statusId) && statusId > 0) {
		return statusId === 7;
	}

	const status = String(order.status || '').toLowerCase();
	return status.includes('отказан');
}

export function findMetadataIdByLabel(metadataObject, label) {
	if (!metadataObject || label === null || label === undefined || label === '') {
		return null;
	}
	const normalizedLabel = String(label).trim();
	const entry = Object.entries(metadataObject).find(([, name]) => String(name).trim() === normalizedLabel);
	if (!entry) {
		return null;
	}
	return /^\d+$/.test(entry[0]) ? Number(entry[0]) : entry[0];
}

function resolveMetadataOrNumericId(value, metadataObject) {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	if (typeof value === 'number' || /^\d+$/.test(String(value))) {
		return Number(value);
	}
	return findMetadataIdByLabel(metadataObject, value);
}

function stripPhoneDigits(phone) {
	if (!phone) {
		return null;
	}
	const digits = String(phone).replace(/\D/g, '');
	return digits || null;
}

function findListItemIdByName(list, label) {
	if (!label || !Array.isArray(list)) {
		return null;
	}
	const normalizedLabel = String(label).trim();
	const match = list.find((item) => String(item.name).trim() === normalizedLabel);
	return match ? match.id : null;
}

function findListItemId(list, id) {
	if (id === null || id === undefined || !Array.isArray(list)) {
		return null;
	}
	const match = list.find((item) => String(item.id) === String(id));
	return match ? match.id : null;
}

export function resolveQuotaTypeId(order, quotaList = [], metadata = {}) {
	if (order.quota_type_id != null && order.quota_type_id !== '' && order.quota_type_id !== 0 && order.quota_type_id !== '0') {
		return findListItemId(quotaList, order.quota_type_id) ?? order.quota_type_id;
	}
	if (order.quota_type) {
		return findListItemIdByName(quotaList, order.quota_type)
			?? findMetadataIdByLabel(metadata.quota_type || metadata.quota, order.quota_type)
			?? findListItemId(quotaList, '0')
			?? '0';
	}
	return findListItemId(quotaList, '0') ?? '0';
}

function findEducationDocumentId(relations, educationTypeId, label) {
	if (!label) {
		return null;
	}
	const documents = mapRelationsDocuments(relations, educationTypeId);
	return findListItemIdByName(documents, label);
}

export function mapInfoOrderToEditData(order, context = {}) {
	const { metadata = {}, nationalityList = [], relations = [], quotaCategoryList = [] } = context;
	const educationSource = getOrderEducationSource(order);

	let schoolId = order.school_id;
	if ((schoolId === null || schoolId === undefined) && order.school && typeof order.school === 'object') {
		schoolId = order.school.id ?? null;
	}

	const educationTypeId = resolveMetadataOrNumericId(
		order.education_type_id ?? order.education_type,
		metadata.education_type
	);

	const regionAb = order.region_id
		?? order.region_ab
		?? findListItemIdByName(context.regionList, order.region);

	return {
		payment_type_id: resolveMetadataOrNumericId(
			order.payment_type_id ?? order.payment_type,
			metadata.payment_type
		),
		training_type_id: resolveMetadataOrNumericId(
			order.training_type_id ?? order.training_type,
			metadata.training_type
		),
		education_form_id: resolveMetadataOrNumericId(
			order.education_form_id ?? order.education_form,
			metadata.education_form
		),
		region_ab: regionAb,
		declarant_type_id: order.declarant_type != null ? Number(order.declarant_type) : null,
		citizen_status_id: resolveMetadataOrNumericId(
			order.citizen_status_id ?? order.citizen_status,
			metadata.citizen_status
		),
		nationality_id: order.nationality_id != null
			? Number(order.nationality_id)
			: findListItemIdByName(nationalityList, order.nationality),
		iin: order.abiturient_iin || null,
		surname: order.abiturient_surname || '',
		name: order.abiturient_name || '',
		lastname: order.abiturient_lastname || '',
		birthday: order.abiturient_date || null,
		email: order.abiturient_email || '',
		phone_number: stripPhoneDigits(order.abiturient_phone),
		parent_phone_number: stripPhoneDigits(order.parent_phone ?? order.user?.parent_phone),
		school_id: schoolId,
		school_country_id: order.school_country_id
			?? resolveMetadataOrNumericId(order.school_country, metadata.country),
		school_region_ab: getSchoolRegionId(educationSource) ?? null,
		graduation_year: order.graduation_year != null ? String(order.graduation_year) : null,
		education_type_id: educationTypeId,
		education_document_type_id: order.education_document_id
			?? resolveMetadataOrNumericId(order.education_document, metadata.education_document)
			?? findEducationDocumentId(relations, educationTypeId, order.education_document),
		quota_list: [{ id: resolveQuotaTypeId(order, quotaCategoryList, metadata) }],
		choosed_qualification: mapOrderQualificationsToEdit(order.request_qualifications, metadata),
	};
}

export function resolveStudyLangShort(lang, metadata) {
	if (!lang) {
		return null;
	}
	if (String(lang).length <= 3) {
		return lang;
	}
	const normalizedLang = String(lang).trim();
	const entry = Object.entries(metadata.study_lang || {}).find(([, name]) => String(name).trim() === normalizedLang);
	return entry ? entry[0] : lang;
}

export function mapOrderQualificationsToEdit(qualifications, metadata = {}) {
	if (!Array.isArray(qualifications)) {
		return [];
	}
	return qualifications.map((qualification) => {
		const code = qualification.code || qualification.qualification_code || '';
		const { specialty_code: specialtyCode } = parseQualificationCodeKey(code);
		return {
			request_qualification_id: qualification.id ?? null,
			specialty_code: qualification.specialty_code || specialtyCode || '',
			code,
			name: qualification.name || '',
			qualification_id: qualification.qualification_id ?? null,
			college_id: qualification.college_id ?? null,
			college_name: qualification.college_name || qualification.college || null,
			college_bin: qualification.college_bin ?? qualification.bin ?? null,
			lang: resolveStudyLangShort(
				qualification.lang_short ?? qualification.lang_code ?? qualification.lang,
				metadata
			),
			bin: qualification.college_bin ?? qualification.bin ?? null,
		};
	});
}

export function buildQualificationItemFromOrder(chosen, metadata = {}) {
	const code = chosen.code || '';
	const { specialty_code: specialtyCode, display_codes: displayCodes } = parseQualificationCodeKey(code);
	const langShort = resolveStudyLangShort(chosen.lang, metadata);
	const colleges = [];

	if (chosen.college_name || chosen.college_bin || chosen.college_id) {
		colleges.push({
			college_id: chosen.college_id || chosen.college_bin,
			name: chosen.college_name || String(chosen.college_bin || ''),
			lang: langShort ? [langShort] : [],
		});
	}

	return {
		qualification_code: code,
		specialty_code: specialtyCode || chosen.specialty_code || '',
		qualification_id: chosen.qualification_id,
		name: chosen.name || '',
		display_label: chosen.name ? `${displayCodes} - ${chosen.name}` : displayCodes,
		colleges,
	};
}

export function resolveCollegeIdFromOrder(colleges, chosen) {
	if (!Array.isArray(colleges) || !colleges.length) {
		return chosen.college_id || null;
	}
	if (chosen.college_id && colleges.some((college) => college.college_id === chosen.college_id)) {
		return chosen.college_id;
	}
	if (chosen.college_name) {
		const byName = colleges.find((college) => college.name === chosen.college_name);
		if (byName) {
			return byName.college_id;
		}
	}
	if (chosen.college_bin) {
		const byBin = colleges.find((college) => (
			String(college.college_bin || '') === String(chosen.college_bin)
			|| String(college.college_id) === String(chosen.college_bin)
		));
		if (byBin) {
			return byBin.college_id;
		}
	}
	return colleges.length === 1 ? colleges[0].college_id : chosen.college_id;
}

export function buildEducationDisplayFields(order, metadata, t, regionList = []) {
	const normalizedRegionList = prepareRegionListForLookup(regionList);
	const source = getOrderEducationSource(order);
	const fields = [
		{
			label: t('Тип образования'),
			value: getMetadataLabel(metadata.education_type, source.education_type_id ?? source.education_type)
				|| source.education_type
				|| '—',
		},
		{
			label: t('Тип документа о полученном образовании'),
			value: source.education_document || '—',
		},
		{
			label: t('Год окончания (выпуска)'),
			value: source.graduation_year || '—',
		},
	];
	if (isKazakhstanSchoolCountry(getOrderSchoolCountry(order))) {
		const schoolDisplay = getSchoolEducationDisplayInfo(order, normalizedRegionList);
		fields.splice(1, 0, {
			label: t('Регион окончания школы'),
			value: normalizeDisplayValue(schoolDisplay.regionName),
		});
		if (schoolDisplay.showSchoolName) {
			fields.splice(2, 0, {
				label: t('Наименование школы'),
				value: normalizeDisplayValue(schoolDisplay.schoolName),
			});
		}
	}
	return fields;
}

export function mapInfoOrderToView(order, t, options = {}) {
	const { regionList = [] } = options;
	const normalizedRegionList = prepareRegionListForLookup(regionList);
	const infoValues = {
		request_number: infoOrderField(t('Номер заявки'), order.id),
		status: infoOrderField(t('Статус заявки'), order.status),
	};
	const rejectReason = getOrderRejectReason(order);
	if (rejectReason) {
		infoValues.reject_reason = {
			...infoOrderField(t('Причина отказа'), rejectReason),
			labelClass: 'error--text',
		};
	}
	const info = {
		name: t('Информация о заявке'),
		values: infoValues,
	};

	const block = [
		{
			name: t('Основные персональные данные Абитуриента'),
			values: {
				iin: infoOrderField(t('ИИН'), order.abiturient_iin),
				fio: infoOrderField(
					t('ФИО'),
					formatInfoOrderFullName([
						order.abiturient_surname,
						order.abiturient_name,
						order.abiturient_lastname,
					])
				),
				birthday: infoOrderField(t('День рождения'), formatInfoOrderDate(order.abiturient_date)),
				nationality: infoOrderField(t('Национальность'), order.nationality),
				citizen_status: infoOrderField(t('Статус гражданина'), order.citizen_status),
				form_086: infoOrderFileField(
					t('Медицинская справка 075/У (086)'),
					order.form_086 || order.form086
				),
			},
		},
	];

	const declarantType = Number(order.declarant_type);
	const contactValues = {
		email: infoOrderField(t('Электронная почта'), order.abiturient_email),
		phone: infoOrderField(t('Номер телефона'), order.abiturient_phone),
	};

	if ([2, 3, 4].includes(declarantType) && order.user) {
		block.push({
			name: t('Данные заявителя'),
			values: {
				fio: infoOrderField(
					t('ФИО'),
					formatInfoOrderFullName([order.user.surname, order.user.name, order.user.lastname])
				),
				birthday: infoOrderField(t('День рождения'), formatInfoOrderDate(order.user.birthday)),
				parent_phone: infoOrderField(
					t('Номер телефона родителей'),
					order.user.parent_phone ?? order.parent_phone
				),
			},
		});
	} else {
		contactValues.parent_phone = infoOrderField(t('Номер телефона родителей'), order.parent_phone);
	}

	block.push({
		name: t('Контактные данные'),
		values: contactValues,
	});

	const educationSource = getOrderEducationSource(order);
	const educationValues = {
		education_type: infoOrderField(t('Тип образования'), educationSource.education_type),
		education_document: infoOrderField(t('Тип документа о полученном образовании'), educationSource.education_document),
		document_file: infoOrderFileField(t('Документ об образовании'), order.document_file),
		school_country: infoOrderField(t('Страна окончания школы'), educationSource.school_country),
		graduation_year: infoOrderField(t('Год окончания (выпуска)'), educationSource.graduation_year),
	};
	if (isKazakhstanSchoolCountry(getOrderSchoolCountry(order))) {
		const schoolDisplay = getSchoolEducationDisplayInfo(order, normalizedRegionList);
		educationValues.school_region = infoOrderField(
			t('Регион окончания школы'),
			schoolDisplay.regionName,
		);
		if (schoolDisplay.showSchoolName) {
			educationValues.school = infoOrderField(
				t('Наименование школы'),
				schoolDisplay.schoolName,
			);
		}
	}
	block.push({
		name: t('Данные об образовании'),
		values: educationValues,
	});

	block.push({
		name: t('Квота приема'),
		values: {
			quota_type: infoOrderField(t('Тип квоты приема'), order.quota_type || t('Без льготы')),
			quota_document: infoOrderFileField(t('Документ о квоте'), order.quota),
		},
	});

	(order.request_qualifications || []).forEach((qualification) => {
		const values = {
			name: infoOrderField(t('Наименование квалификации'), qualification.name),
			code: infoOrderField(t('Код квалификации'), formatQualificationCodeDisplay(qualification.code)),
			college_name: infoOrderField(
				t('Наименование колледжа'),
				qualification.college_name || qualification.college
			),
			college_bin: infoOrderField(t('БИН колледжа'), qualification.college_bin || qualification.bin),
			status: infoOrderField(t('Статус'), qualification.status),
			lang: infoOrderField(t('Язык обучения'), qualification.lang),
			target_grant: infoOrderFlagField(t('Участвует в целевом гранте'), qualification.target_grant, t),
			exam: infoOrderFlagField(t('Экзамен'), qualification.exam, t),
		};
		block.push({
			name: `${t('Квалификация №')}${qualification.counter}`,
			values,
		});
	});

	return {
		info,
		block,
		canWithdraw: canWithdrawOrder(order),
		canEdit: canEditOrder(order),
	};
}

export function mapRelationsDocuments(relations, educationTypeId) {
	if (!Array.isArray(relations) || educationTypeId === null || educationTypeId === undefined) {
		return [];
	}
	const relation = relations.find((item) => item.education_type === Number(educationTypeId));
	if (!relation || !Array.isArray(relation.documents)) {
		return [];
	}
	return relation.documents.map((doc) => ({
		id: doc.education_document,
		name: doc.name,
	}));
}
